/**
 * Custom element that wraps a <video> tag to ensure robust autoplay
 * behavior across all browsers.
 *
 * Regular browsers (Safari, Chrome, Firefox, etc.):
 * - Explicit play() with retry and backoff
 * - IntersectionObserver and visibilitychange re-triggers
 * - User gesture fallback for stubborn browsers
 *
 * TikTok and other in-app browsers that hijack video playback:
 * - These browsers block inline video entirely. They patch play() at
 *   the JS level and block the HTML autoplay attribute.
 * - We leave the video completely untouched — no JS interaction at all.
 * - The video's first frame displays naturally as a static hero image.
 * - This is the only reliable behavior in TikTok's WebView.
 */
class HeroVideo extends HTMLElement {
  static HOSTILE_BROWSERS = /musical_ly|tiktok|bytedance|snapchat/i;

  connectedCallback() {
    this.video = this.querySelector('video');
    if (!this.video) return;

    // TikTok's in-app browser hijacks any interaction with <video> elements.
    // The safest thing is to do absolutely nothing — let the browser render
    // the first frame naturally via the HTML attributes already on the tag.
    if (HeroVideo.HOSTILE_BROWSERS.test(navigator.userAgent)) {
      this.video.removeAttribute('controls');
      return;
    }

    this.initStandardPlayer();
  }

  /**
   * Standard path for regular browsers. Unchanged from the working version.
   */
  initStandardPlayer() {
    this.maxRetries = 3;
    this.retryCount = 0;
    this.playing = false;

    this.video.muted = true;
    this.video.playsInline = true;
    this.video.setAttribute('playsinline', '');
    this.video.setAttribute('webkit-playsinline', '');
    this.video.setAttribute('muted', '');
    this.video.removeAttribute('controls');

    this.video.addEventListener('playing', () => {
      this.playing = true;
      this.retryCount = 0;
    });

    this.video.addEventListener('pause', () => {
      this.playing = false;
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.playing) {
            this.attemptPlay();
          }
        }
      },
      { threshold: 0.1 }
    );
    this.observer.observe(this.video);

    this.onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !this.playing) {
        this.retryCount = 0;
        this.attemptPlay();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.onUserInteraction = () => {
      if (!this.playing) {
        this.attemptPlay();
      }
    };
    document.addEventListener('touchstart', this.onUserInteraction, { once: true, passive: true });
    document.addEventListener('click', this.onUserInteraction, { once: true });

    this.attemptPlay();
  }

  attemptPlay() {
    if (!this.video || this.playing) return;

    const playPromise = this.video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        this.retryCount++;
        if (this.retryCount <= this.maxRetries) {
          const delay = 500 * Math.pow(2, this.retryCount - 1);
          setTimeout(() => this.attemptPlay(), delay);
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('touchstart', this.onUserInteraction);
    document.removeEventListener('click', this.onUserInteraction);
  }
}

if (!customElements.get('hero-video')) {
  customElements.define('hero-video', HeroVideo);
}
